// Discover step — SCAN body. Backtests every condition combination from the indicators picked
// in Setup (at the chosen depth), ranks them, and assembles a strategy from the results. No
// manual condition picking here (that's ManualConditionPicker).
//
// A strategy can hold BOTH a long side AND a short side at once. A "both" scan auto-assembles the
// best long candidate + the best short candidate; the user can override either side by picking
// another row. Combining sides is sound because every scan candidate indexes the SAME condition
// list (built from the draft's shared indicatorSpecs).
//
// LAYOUT: a persistent two-column split — the ranked LIST + scan controls live on the LEFT, and a
// sticky CHART/RESULTS inspector lives on the RIGHT. The right pane shows the full backtest report
// once the user hits Backtest (bt.open), otherwise the selected row's simulated-trade chart. The
// shared backtest state (useDraftBacktest) is owned by the parent StepDiscover and passed in as `bt`.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, ArrowRight, Trophy, LineChart, Check, BarChart3, AlertTriangle, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useBuilderState } from "../../../state/BuilderState";
import { runScan } from "../../../api/scan";
import { biasChipClass } from "./conditionBias";
import { CandidatePreviewChart } from "./CandidatePreviewChart";
import { BacktestPane } from "./BacktestPane";
import type { DraftBacktestState } from "../../../hooks/useDraftBacktest";
import type { BacktestDirection, ScanResultDto } from "../../../api/types";

// A row backing the ranked table — one scan candidate.
interface DiscoverRow {
  key: string;
  result: ScanResultDto;
}

// Entry conditions are ANDed — every one must fire on the same bar. "∧" reads as that.
function conditionSummary(c: ScanResultDto): string {
  if (!c.conditions || c.conditions.length === 0) return "—";
  return c.conditions.map((x) => x.name).join("  ∧  ");
}

interface Props {
  /** Shared backtest state (owned by StepDiscover; drives the right-hand results pane). */
  bt: DraftBacktestState;
  /** Advance to the Activate step (from the report's "Use this strategy"). */
  onAdvance: () => void;
}

const DEPTHS = [1, 2, 3] as const;
const DIRECTIONS: { id: BacktestDirection; label: string }[] = [
  { id: "both", label: "Both" },
  { id: "long", label: "Long" },
  { id: "short", label: "Short" },
];

export function StepScan({ bt, onAdvance }: Props) {
  const { draft, setDraft } = useBuilderState();
  const [depth, setDepth] = useState<number>(3);          // preselect 3 conditions
  const [direction, setDirection] = useState<BacktestDirection>("both"); // preselect Both
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState<DiscoverRow[] | null>(null);
  const [meta, setMeta] = useState<{
    label: string;
    truncated: boolean;
    noProfitable?: boolean;
    depthUnused?: boolean;
  } | null>(null);
  // The row whose trades are shown on the preview chart (click a row to inspect).
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  // Rows committed to each side (separate so BOTH a long and a short pick can show ✓ at once).
  const [usedLongKey, setUsedLongKey] = useState<string | null>(null);
  const [usedShortKey, setUsedShortKey] = useState<string | null>(null);

  const hasCoin = draft.coin.trim().length > 0;
  const canScan = hasCoin && draft.indicatorSpecs.length > 0;
  const previewRow = rows?.find((r) => r.key === previewKey) ?? null;

  // Assembled strategy = whatever the draft currently holds (single source of truth).
  const hasLong = draft.longCondIndices.length > 0;
  const hasShort = draft.shortCondIndices.length > 0;
  const hasStrategy = hasLong || hasShort;

  // Risk limits active ⇒ the scan ranks (and charts) WITH SL/TP/trailing — EvalCombo applies them
  // per candidate, so the list metrics reflect your stops, not a conditions-only score. Surface it.
  const r = draft.riskSettings;
  const riskActive = (r?.stopLossPercent ?? 0) > 0 || (r?.takeProfitPercent ?? 0) > 0 || (r?.trailingStopPercent ?? 0) > 0;

  // Invalidate a stale backtest report whenever the assembled strategy changes.
  const onStrategyChanged = bt.reset;

  // Bring the full-width report into view when a backtest opens (it renders below the list).
  const reportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (bt.open) reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [bt.open]);

  const resetResults = () => {
    setRows(null);
    setPreviewKey(null);
    setUsedLongKey(null);
    setUsedShortKey(null);
    setMeta(null);
    onStrategyChanged();
  };

  const handleScan = async () => {
    setRunning(true);
    resetResults();
    try {
      const res = await runScan({
        coin: draft.coin,
        interval: draft.interval,
        marketType: draft.marketType,
        indicatorSpecs: draft.indicatorSpecs,
        direction,
        depth,
        minOccurrences: 5,
        useAdvanced: draft.useAdvanced,
        usePriceAction: draft.usePriceAction,
        forwardBars: draft.forwardBars,
        exitMode: draft.exitMode,
        topN: 25,
        // Rank candidates WITH the user's risk limits (SL/TP/trailing) so the list reflects how the
        // full Backtest will execute them — not a conditions-only score. Null/≤0 fields = disabled.
        riskSettings: draft.riskSettings ?? null,
      });
      const built: DiscoverRow[] = res.candidates.map((c, i) => ({ key: `scan-${i}`, result: c }));
      setRows(built);

      // A candidate scores 0 when it's net-losing (the scorer floors losers at 0). Only auto-adopt
      // a side that's actually worth something — never present a losing combo as a green ✓ "pick".
      const profitable = (r: DiscoverRow) => r.result.score > 0;
      // Depth>1 but every result is single-condition ⇒ the user's indicator set can't form
      // multi-condition combos (e.g. one indicator → same-instance pairs are skipped). Hint it.
      const allSingleCond = built.length > 0 && built.every((r) => r.result.condIndices.length === 1);
      const depthUnused = depth > 1 && allSingleCond;

      setMeta({
        label: `${res.combosEvaluated} combinations backtested`,
        truncated: res.truncated,
        noProfitable: built.length > 0 && !built.some(profitable),
        depthUnused,
      });

      if (built.length === 0) {
        toast.info("No combinations met the trade threshold", {
          description: "Try a different timeframe, more indicators, or a lower depth.",
        });
      } else {
        setPreviewKey(built[0]!.key);
        // Auto-assemble: best PROFITABLE long + best PROFITABLE short (rows are score-ranked desc).
        // Both index the SAME condition list (shared indicatorSpecs), so they compose into one
        // strategy. If a side has no profitable candidate, leave it empty rather than adopt a loser.
        const bestLong = built.find((r) => r.result.direction === "long" && profitable(r));
        const bestShort = built.find((r) => r.result.direction === "short" && profitable(r));
        if (bestLong || bestShort) {
          setDraft((prev) => ({
            ...prev,
            longCondIndices: bestLong ? [...bestLong.result.condIndices] : [],
            shortCondIndices: bestShort ? [...bestShort.result.condIndices] : [],
            // Adopt the exits the scanner auto-discovered for each side (Phase-2 exit scan). Empty
            // when none were found → the strategy exits via ExitMode.
            exitLongCondIndices: bestLong?.result.exitCondIndices ? [...bestLong.result.exitCondIndices] : [],
            exitShortCondIndices: bestShort?.result.exitCondIndices ? [...bestShort.result.exitCondIndices] : [],
          }));
          setUsedLongKey(bestLong?.key ?? null);
          setUsedShortKey(bestShort?.key ?? null);
          const assembled = [bestLong && "best long", bestShort && "best short"].filter(Boolean).join(" + ");
          toast.success(`Found ${built.length} candidates`, {
            description: `Assembled ${assembled}. Backtest or override a side.`,
          });
        } else {
          // Results exist but none are profitable — show them, but don't pretend one is "the pick".
          toast.info(`No profitable strategy found`, {
            description: "All combinations lost money on this data. Try other indicators or a timeframe.",
          });
        }
      }
    } catch (err) {
      toast.error("Scan failed", { description: (err as Error).message });
    } finally {
      setRunning(false);
    }
  };

  // Adopt a row into the strategy. Fills ONLY this candidate's own side (long/short) and leaves
  // the opposite side intact, so the user can assemble both sides into one strategy.
  const useRow = (row: DiscoverRow) => {
    const dir = row.result.direction;
    setDraft((prev) => {
      const next = { ...prev };
      // Fill this side's entry + its auto-discovered exit; leave the opposite side intact.
      const discoveredExit = row.result.exitCondIndices ? [...row.result.exitCondIndices] : [];
      if (dir === "short") {
        next.shortCondIndices = [...row.result.condIndices];
        next.exitShortCondIndices = discoveredExit;
      } else {
        next.longCondIndices = [...row.result.condIndices];
        next.exitLongCondIndices = discoveredExit;
      }
      return next;
    });
    if (dir === "short") setUsedShortKey(row.key);
    else setUsedLongKey(row.key);
    // Chart the strategy you just selected — picking a side (or switching it) snaps the trade
    // preview to that row, so the chart always reflects the current long/short selection.
    setPreviewKey(row.key);
    toast.success(`${dir === "short" ? "Short" : "Long"} side set`, {
      description: "Assemble the other side, backtest, or continue.",
    });
    onStrategyChanged();
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-bold">Find a strategy by scanning</h2>
        <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
          Backtests every combination of the indicators you picked and assembles the best{" "}
          <span className="text-emerald-400 font-semibold">long</span> and{" "}
          <span className="text-red-400 font-semibold">short</span> side into one strategy on{" "}
          <span className="font-mono">{draft.coin || "—"} {draft.interval}</span>. Pick rows to set each side, then{" "}
          <span className="font-semibold text-foreground">Backtest</span> the combined result.
        </p>
      </div>

      {/* Two columns: ranked list + controls (left), sticky chart/results inspector (right).
          The list gets the larger share (1.5fr vs 1fr) so the wide results table shows all its
          columns without clipping; the chart still gets a comfortable pane. Splits at lg (1024px). */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-5">
        {/* ───────── LEFT: controls + ranked list ───────── */}
        <div className="flex flex-col gap-4 min-w-0">
          {/* Controls */}
          <Card className="p-4 bg-card flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground/60 block mb-1.5">
                Conditions per strategy
              </Label>
              <div className="flex gap-1.5">
                {DEPTHS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDepth(d)}
                    disabled={running}
                    className={cn(
                      "size-9 rounded-xl border-2 font-mono font-bold text-sm transition-all disabled:opacity-50",
                      depth === d ? "border-primary bg-primary/10 text-primary" : "border-border bg-card-elevated hover:border-primary/50",
                    )}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground/60 block mb-1.5">
                Direction
              </Label>
              <div className="flex gap-1.5">
                {DIRECTIONS.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDirection(d.id)}
                    disabled={running}
                    className={cn(
                      "px-3 h-9 rounded-xl border-2 text-xs font-semibold transition-all disabled:opacity-50",
                      direction === d.id ? "border-primary bg-primary/10 text-primary" : "border-border bg-card-elevated hover:border-primary/50",
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <Button
              onClick={handleScan}
              disabled={!canScan || running}
              className="ml-auto bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {running ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
              {running ? "Scanning…" : rows ? "Re-scan" : "Run scan"}
            </Button>
          </Card>

          {!canScan && (
            <p className="text-xs text-muted-foreground italic">
              Add a coin and at least one indicator in the Setup step to enable scanning.
            </p>
          )}

          {/* No-profitable banner — results exist but every combo lost money. We still list them
              (useful to see what was tried) but don't auto-assemble a loser as "the pick". */}
          {meta?.noProfitable && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300 leading-snug">
              <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
              <span>
                <span className="font-semibold">No profitable strategy found</span> for these settings — every
                combination lost money on this data (score 0). The results below are ranked least-bad first.
                Try more or different indicators, another timeframe, or different exit settings.
              </span>
            </div>
          )}

          {/* Depth hint — user asked for multi-condition strategies but the indicator set can't form
              them (conditions from the same indicator instance are skipped as contradictory). */}
          {meta?.depthUnused && (
            <div className="flex items-start gap-2 rounded-xl border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-[11px] text-muted-foreground leading-snug">
              <Info className="size-3.5 mt-0.5 shrink-0 text-blue-400" />
              <span>
                You picked <span className="font-semibold text-foreground">{depth} conditions per strategy</span>, but
                every result has just one — multi-condition strategies need conditions from{" "}
                <span className="font-semibold text-foreground">different</span> indicators. Add another indicator in
                Setup to combine them.
              </span>
            </div>
          )}

          {running && !rows && (
            <Card className="p-10 text-center bg-card-elevated">
              <Loader2 className="size-6 mx-auto mb-3 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Backtesting every combination over the last 5000 bars…</p>
            </Card>
          )}

          {/* Ranked table */}
          {rows && rows.length > 0 && (
            <Card className="p-0 bg-card overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border flex-wrap">
                <div className="flex items-center gap-2 text-sm font-semibold flex-wrap">
                  <Trophy className="size-4 text-primary" />
                  Ranked strategies
                  <span className="text-[11px] font-normal text-muted-foreground">— click a row to chart its trades</span>
                  {riskActive && (
                    <Badge variant="outline" className="border-primary/40 text-primary text-[10px] font-normal" title="Each candidate was scored with your Setup risk limits (stop-loss / take-profit / trailing) applied intrabar.">
                      ranked with your SL/TP
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {meta && (
                    <span className="text-[11px] text-muted-foreground">
                      {meta.label}{meta.truncated ? " · truncated" : ""}
                    </span>
                  )}
                  <Button
                    size="sm"
                    onClick={() => void bt.run(draft)}
                    disabled={!hasStrategy || running}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    title={hasStrategy ? "Backtest the assembled long + short strategy" : "Assemble a long and/or short side first"}
                  >
                    <BarChart3 className="size-3.5" />
                    Backtest
                  </Button>
                </div>
              </div>

              {/* Assembled-strategy summary — what's currently in each side. */}
              <div className="flex items-center gap-3 px-4 py-2 border-b border-border/60 text-[11px] flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">Assembled</span>
                <span className="inline-flex items-center gap-1">
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 text-[10px]">long</Badge>
                  <span className={cn("font-mono", hasLong ? "text-foreground" : "text-muted-foreground/50")}>
                    {hasLong ? `${draft.longCondIndices.length} cond${draft.longCondIndices.length > 1 ? "s" : ""}` : "none"}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Badge variant="outline" className="border-red-500/40 text-red-400 text-[10px]">short</Badge>
                  <span className={cn("font-mono", hasShort ? "text-foreground" : "text-muted-foreground/50")}>
                    {hasShort ? `${draft.shortCondIndices.length} cond${draft.shortCondIndices.length > 1 ? "s" : ""}` : "none"}
                  </span>
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                    <tr className="border-b border-border">
                      <th className="text-left font-medium px-3 py-2">#</th>
                      <th className="text-left font-medium px-3 py-2">Dir</th>
                      <th className="text-left font-medium px-3 py-2">Rules</th>
                      <th className="text-right font-medium px-3 py-2">Score</th>
                      <th className="text-right font-medium px-3 py-2">Win %</th>
                      <th className="text-right font-medium px-3 py-2">Trades</th>
                      <th className="text-right font-medium px-3 py-2">Total %</th>
                      <th className="text-right font-medium px-3 py-2">PF</th>
                      <th className="text-right font-medium px-3 py-2">Max DD</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const c = row.result;
                      const isPreviewed = previewKey === row.key;
                      const isUsed = usedLongKey === row.key || usedShortKey === row.key;
                      const isShort = c.direction === "short";
                      return (
                        <tr
                          key={row.key}
                          onClick={() => setPreviewKey(row.key)}
                          className={cn(
                            "border-b border-border/40 transition-colors cursor-pointer",
                            isPreviewed ? "bg-primary/10" : "hover:bg-card-elevated",
                          )}
                        >
                          <td className="px-3 py-2 font-mono text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              {isPreviewed && <LineChart className="size-3 text-primary" />}
                              {i + 1}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={cn("font-mono text-[10px]",
                              isShort ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400")}>
                              {c.direction}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 max-w-[240px]">
                            <span className="block truncate text-muted-foreground" title={conditionSummary(c)}>
                              {conditionSummary(c)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-primary">{c.score}</td>
                          <td className="px-3 py-2 text-right font-mono">{c.winRate.toFixed(1)}</td>
                          <td className="px-3 py-2 text-right font-mono">{c.trades}</td>
                          <td className={cn("px-3 py-2 text-right font-mono", c.totalReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                            {c.totalReturn.toFixed(1)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{c.profitFactor.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-mono text-red-400">{c.maxStratDD.toFixed(1)}</td>
                          <td className="px-3 py-2 text-right">
                            <Button size="sm" variant={isUsed ? "default" : "outline"}
                              className={isUsed ? "bg-primary text-primary-foreground" : ""}
                              title={isUsed
                                ? `In use as the ${isShort ? "short" : "long"} side — click to re-apply`
                                : `Use this as the ${isShort ? "short" : "long"} side of your strategy`}
                              onClick={(e) => { e.stopPropagation(); useRow(row); }}>
                              {isUsed ? <><Check className="size-3.5" /> In use</> : <>Use as {isShort ? "short" : "long"} <ArrowRight className="size-3.5" /></>}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {rows && rows.length === 0 && (
            <Card className="p-8 text-center bg-card-elevated text-sm text-muted-foreground">
              No profitable strategy found for these settings. Try a different timeframe or direction,
              add more indicators, or lower the conditions-per-strategy depth.
            </Card>
          )}
        </div>

        {/* ───────── RIGHT: sticky chart / results inspector ───────── */}
        <div className="min-w-0">
          <div className="lg:sticky lg:top-4 flex flex-col gap-4">
            {previewRow ? (
              <Card className="p-4 bg-card space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <LineChart className="size-4 text-primary" />
                    <span className="text-sm font-semibold">Trade preview</span>
                    <Badge variant="outline" className={cn("font-mono text-[10px]",
                      previewRow.result.direction === "short" ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400")}>
                      {previewRow.result.direction}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground font-mono">
                      score {previewRow.result.score} · {previewRow.result.trades} trades · {previewRow.result.totalReturn.toFixed(1)}%
                      {riskActive ? " · SL/TP applied" : ""}
                    </span>
                  </div>
                  <Button size="sm" onClick={() => useRow(previewRow)} variant="outline">
                    Use as {previewRow.result.direction === "short" ? "short" : "long"}
                    <ArrowRight className="size-3.5" />
                  </Button>
                </div>

                {/* The actual rules this candidate fires on — entry conditions are ANDed (all must fire). */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 mr-1">Rules</span>
                  {previewRow.result.conditions.length === 0 ? (
                    <span className="text-[11px] text-muted-foreground">No condition descriptors returned.</span>
                  ) : (
                    previewRow.result.conditions.map((cond, idx) => (
                      <span key={cond.id} className="inline-flex items-center gap-1.5">
                        {idx > 0 && <span className="text-[10px] text-muted-foreground/50 font-mono">∧</span>}
                        <Badge variant="outline" className={cn("font-mono text-[10px]", biasChipClass(cond.bias))}>
                          {cond.name}
                        </Badge>
                      </span>
                    ))
                  )}
                  <span className="text-[10px] text-muted-foreground/70 ml-1">(all must fire to enter)</span>
                </div>

                <CandidatePreviewChart
                  coin={draft.coin}
                  interval={draft.interval}
                  marketType={draft.marketType}
                  candidate={previewRow.result}
                />
              </Card>
            ) : (
              <Card className="p-8 bg-card-elevated min-h-[420px] flex flex-col items-center justify-center text-center gap-2">
                <LineChart className="size-8 text-muted-foreground/40" />
                <p className="text-sm font-medium text-muted-foreground">No strategy charted yet</p>
                <p className="text-xs text-muted-foreground/70 max-w-xs">
                  Run a scan, then click any ranked row to chart its simulated trades here — or hit{" "}
                  <span className="font-semibold text-foreground">Backtest</span> to see the full report for the
                  assembled long + short strategy.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Full-width backtest report — the rich StrategyReport (KPIs, equity, returns, benchmark,
          risk, trade list) rendered at full page width like the Monitor tab, so all the detail is
          readable instead of cramped into the right column. */}
      {bt.open && (
        <div ref={reportRef} className="scroll-mt-4">
          <BacktestPane
            result={bt.result}
            running={bt.running}
            hasStrategy={hasStrategy}
            coin={draft.coin}
            interval={draft.interval}
            marketType={draft.marketType}
            onClose={bt.close}
            onAdvance={onAdvance}
          />
        </div>
      )}
    </div>
  );
}
